import React, { useState, useEffect, useRef } from 'react';
import { 
  Settings as SettingsIcon, 
  Save, 
  Globe, 
  FileText, 
  ShoppingCart, 
  CreditCard,
  Plus,
  Trash2,
  Info,
  RefreshCw,
  TrendingUp,
  Users as UsersIcon,
  ShieldCheck,
  Edit2,
  X,
  Mail,
  UserPlus,
  Sparkles,
  Upload,
  Monitor,
  Smartphone,
  ArrowRight,
  ChevronRight,
  Library,
  Truck,
  ShoppingBag,
  Receipt,
  CheckCircle2
} from 'lucide-react';
import { CustomSelect } from './ui/CustomSelect';
import { db, auth, handleFirestoreError, OperationType, storage, ref, uploadBytes, getDownloadURL } from '../firebase';
import { doc, 
  getDoc, 
  setDoc, 
  serverTimestamp, 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDocs } from '../firebase';
import { notify } from '../services/notify';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getDefaultCurrency } from '../lib/currency';
import { MODULES, COUNTRIES, CITY_MAP } from '../constants';
import UserAccessRightsView from './UserAccessRightsView';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface OrgMembership {
  orgId: string;
  role: 'admin' | 'user' | 'super-admin' | 'owner';
  permissions: string[];
  suspended?: boolean;
}

interface OrgUser {
  id: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  nationality?: string;
  countryCode?: string;
  gender?: 'male' | 'female';
  memberships: OrgMembership[];
  suspended?: boolean;
  createdAt: any;
}

interface OrganizationSettings {
  id?: string;
  defaultCurrency: string;
  defaultPaymentTerms: number;
  allowedCurrencies: string[];
  defaultInvoiceBrandingTheme: string;
  defaultBillBrandingTheme: string;
  invoicePrefix: string;
  nextInvoiceNumber: number;
  posPrefix: string;
  nextPosNumber: number;
  purchaseOrderPrefix: string;
  nextPurchaseOrderNumber: number;
  purchaseBillPrefix: string;
  nextPurchaseBillNumber: number;
  quotePrefix: string;
  nextQuoteNumber: number;
  creditNotePrefix: string;
  nextCreditNoteNumber: number;
  fixedAssetsPrefix: string;
  nextFixedAssetsNumber: number;
  manualJournalPrefix: string;
  nextManualJournalNumber: number;
  companyName?: string;
  legalName?: string;
  taxNumber?: string;
  registrationNumber?: string;
  industry?: string;
  businessStructure?: string;
  description?: string;
  logoUrl?: string;
  mailingAddress?: string;
  country?: string;
  city?: string;
  attention?: string;
  phoneNumbers?: { countryCode: string; number: string }[];
  fax?: { code: string; number: string };
  email?: string;
  website?: string;
  facebook?: string;
  twitter?: string;
  linkedIn?: string;
  defaultAccounts?: {
    salesAccount?: string;
    accountsReceivable?: string;
    accountsPayable?: string;
    inventoryAccount?: string;
    cogsAccount?: string;
    vatInput?: string;
    vatOutput?: string;
    vatPayable?: string;
    bankCash?: string;
    purchaseAccount?: string;
    salesReturns?: string;
    purchaseReturns?: string;
    discountGiven?: string;
    discountReceived?: string;
  };
  payroll?: unknown;
  layouts?: {
    quotationThemeId?: string;
    invoiceThemeId?: string;
    deliveryThemeId?: string;
    purchaseOrderThemeId?: string;
    billThemeId?: string;
  };
}

interface BankAccountDetails {
  label?: string;
  bankDetails: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  swift: string;
}

interface BrandingTheme {
  id: string;
  name: string;
  logoUrl: string;
  primaryColor: string;
  fontFamily: string;
  invoiceFooterText: string;
  organizationDetails: string;
  logoOverrideUrl?: string;
  logoSize?: 'Small' | 'Medium' | 'Large';
  logoAlignment?: 'Left' | 'Center' | 'Right';
  invoiceLayout?: Record<string, unknown>;
  billLayout?: Record<string, unknown>;
  infoAlignment?: 'Left' | 'Center' | 'Right';
  showBankDetails?: boolean;
  bankAccounts?: BankAccountDetails[];
  paymentInstructions?: string;
  enableWatermark?: boolean;
  watermarkType?: 'Text Only' | 'Image';
  watermarkOpacity?: number;
  autoStatusText?: boolean;
  createdAt: any;
  updatedAt: any;
}

const DEFAULT_SETTINGS: OrganizationSettings = {
  defaultCurrency: 'EGP',
  defaultPaymentTerms: 30,
  allowedCurrencies: [],
  defaultInvoiceBrandingTheme: 'Standard',
  defaultBillBrandingTheme: 'Standard',
  invoicePrefix: 'INV-',
  nextInvoiceNumber: 1,
  posPrefix: 'Receipt Number-',
  nextPosNumber: 1,
  purchaseOrderPrefix: 'PO-',
  nextPurchaseOrderNumber: 1,
  purchaseBillPrefix: 'BILL-',
  nextPurchaseBillNumber: 1,
  quotePrefix: 'QT-',
  nextQuoteNumber: 1,
  creditNotePrefix: 'CN-',
  nextCreditNoteNumber: 1,
  fixedAssetsPrefix: 'FA-',
  nextFixedAssetsNumber: 1,
  manualJournalPrefix: 'MJ-',
  nextManualJournalNumber: 1,
  companyName: '',
  legalName: '',
  taxNumber: '',
  registrationNumber: '',
  industry: '',
  businessStructure: '',
  description: '',
  logoUrl: '',
  mailingAddress: '',
  country: '',
  city: '',
  attention: '',
  phoneNumbers: [{ countryCode: '+20', number: '' }],
  fax: { code: '+20', number: '' },
  email: '',
  website: '',
  facebook: '',
  twitter: '',
  linkedIn: '',
  defaultAccounts: {
    salesAccount: '4010',
    accountsReceivable: '1100',
    accountsPayable: '2010',
    inventoryAccount: '1200',
    cogsAccount: '5010',
    vatInput: '1400',
    vatOutput: '2100',
    vatPayable: '2110',
    bankCash: '1020',
    purchaseAccount: '5020',
    salesReturns: '4040',
    purchaseReturns: '5030',
    discountGiven: '4030',
    discountReceived: '6140'
  },
  layouts: {
    quotationThemeId: 'Standard',
    invoiceThemeId: 'Standard',
    deliveryThemeId: 'Standard',
    purchaseOrderThemeId: 'Standard',
    billThemeId: 'Standard'
  }
};

const WORLD_CURRENCIES = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'QAR', name: 'Qatari Rial' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'LBP', name: 'Lebanese Pound' },
  { code: 'ILS', name: 'Israeli New Shekel' },
].sort((a, b) => a.name.localeCompare(b.name));

const COUNTRY_CODES = [
  { code: '+20', country: 'Egypt' },
  { code: '+971', country: 'UAE' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+1', country: 'USA/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+39', country: 'Italy' },
  { code: '+81', country: 'Japan' },
  { code: '+86', country: 'China' },
  { code: '+91', country: 'India' },
  { code: '+7', country: 'Russia' },
  { code: '+34', country: 'Spain' },
  { code: '+61', country: 'Australia' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+27', country: 'South Africa' },
  { code: '+82', country: 'South Korea' },
  { code: '+65', country: 'Singapore' },
  { code: '+31', country: 'Netherlands' },
  { code: '+41', country: 'Switzerland' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+45', country: 'Denmark' },
  { code: '+353', country: 'Ireland' },
  { code: '+32', country: 'Belgium' },
  { code: '+43', country: 'Austria' },
  { code: '+351', country: 'Portugal' },
  { code: '+30', country: 'Greece' },
  { code: '+90', country: 'Turkey' },
  { code: '+92', country: 'Pakistan' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+62', country: 'Indonesia' },
  { code: '+60', country: 'Malaysia' },
  { code: '+66', country: 'Thailand' },
  { code: '+63', country: 'Philippines' },
  { code: '+84', country: 'Vietnam' },
  { code: '+974', country: 'Qatar' },
  { code: '+965', country: 'Kuwait' },
  { code: '+973', country: 'Bahrain' },
  { code: '+968', country: 'Oman' },
  { code: '+962', country: 'Jordan' },
  { code: '+961', country: 'Lebanon' },
].sort((a, b) => a.country.localeCompare(b.country));

interface SettingsViewProps {
  orgId: string;
  language: 'en' | 'ar';
}

const SettingsView: React.FC<SettingsViewProps> = ({ orgId, language = 'en' }) => {
  const [activeTab, setActiveTab] = useState<'organization' | 'defaults' | 'staff' | 'catalog' | 'invoice-layouts' | 'access-rights'>('organization');
  const [settings, setSettings] = useState<OrganizationSettings>(DEFAULT_SETTINGS);
  const [catalogSettings, setCatalogSettings] = useState<{
    theme: 'Standard' | 'Brutalist' | 'Minimal' | 'Technical' | 'Cherry Blossom';
    customHeroTitle: string;
    customHeroDescription: string;
    primaryColor: string;
    accentColor: string;
    fontFamily: string;
    hidePrices: boolean;
    showInventory: boolean;
    whatsappNumber?: string;
  }>({
    theme: 'Brutalist',
    customHeroTitle: '',
    customHeroDescription: '',
    primaryColor: '#000000',
    accentColor: '#FFFFFF',
    fontFamily: 'Inter',
    hidePrices: false,
    showInventory: true,
    whatsappNumber: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCurrency, setNewCurrency] = useState('');
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [lastRatesUpdate, setLastRatesUpdate] = useState<Date | null>(null);
  const [fetchingRates, setFetchingRates] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);

  useEffect(() => {
    if (!orgId) return;
    const localToken = getLocalAuthToken();
    if (localToken) {
      let cancelled = false;
      void fetch(`/api/organizations/${orgId}/chart-of-accounts`, {
        headers: { Authorization: `Bearer ${localToken}` }
      })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load chart of accounts: ${response.status}`);
          return response.json();
        })
        .then(localAccounts => {
          if (!cancelled) setAccounts(Array.isArray(localAccounts) ? localAccounts : []);
        })
        .catch(error => {
          if (!cancelled) {
            console.error('Local API error loading chart of accounts:', error);
            setAccounts([]);
          }
        });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    return onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAccounts(docs);
      
      // Auto-seed if completely empty
      if (docs.length === 0 && !loading && !saving) {
        seedBasicAccounts();
      }
    });
  }, [orgId]);

  // Staff Management State
  const [staffList, setStaffList] = useState<OrgUser[]>([]);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<OrgUser | null>(null);
  const [newStaff, setNewStaff] = useState({
    fullName: '',
    email: '',
    role: 'user' as OrgMembership['role'],
    permissions: ['dashboard'] as string[]
  });

  const [organization, setOrganization] = useState<any>(null);

  // Branding Themes State
  const [brandingThemes, setBrandingThemes] = useState<BrandingTheme[]>([]);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<BrandingTheme | null>(null);
  const [newTheme, setNewTheme] = useState<{
    name: string;
    logoUrl: string;
    primaryColor: string;
    fontFamily: string;
    invoiceFooterText: string;
    organizationDetails: string;
    logoOverrideUrl: string;
    logoSize: 'Small' | 'Medium' | 'Large';
    logoAlignment: 'Left' | 'Center' | 'Right';
    infoAlignment: 'Left' | 'Center' | 'Right';
    showBankDetails: boolean;
    bankAccounts: BankAccountDetails[];
    paymentInstructions: string;
    enableWatermark: boolean;
    watermarkType: 'Text Only' | 'Image';
    watermarkOpacity: number;
    autoStatusText: boolean;
  }>({
    name: '',
    logoUrl: '',
    primaryColor: '#000000',
    fontFamily: 'Helvetica, Arial, sans-serif',
    invoiceFooterText: '',
    organizationDetails: '',
    logoOverrideUrl: '',
    logoSize: 'Medium',
    logoAlignment: 'Right',
    infoAlignment: 'Left',
    showBankDetails: false,
    bankAccounts: [],
    paymentInstructions: '',
    enableWatermark: false,
    watermarkType: 'Text Only',
    watermarkOpacity: 8,
    autoStatusText: true
  });

  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 200KB for Base64 in Firestore)
    if (file.size > 200 * 1024) {
      notify.error('Logo file size must be less than 200KB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setNewTheme(prev => ({ ...prev, logoUrl: base64 }));
      notify.confirm('Logo uploaded successfully');
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (orgId) {
      fetchSettings();
      const unsubscribeStaff = fetchStaff();
      const unsubscribeThemes = fetchBrandingThemes();
      return () => {
        if (unsubscribeStaff) unsubscribeStaff();
        if (unsubscribeThemes) unsubscribeThemes();
      };
    }
  }, [orgId]);

  // Auto-seed default accounts when accounts list is loaded
  useEffect(() => {
    if (accounts.length > 0 && settings.defaultAccounts) {
      let changed = false;
      const newDefaults = { ...settings.defaultAccounts };
      
      // Check for missing mappings and try to auto-fill if the account exists
      Object.entries(DEFAULT_SETTINGS.defaultAccounts || {}).forEach(([key, defaultValue]) => {
        if (!newDefaults[key as keyof typeof newDefaults]) {
          const matchingAccount = accounts.find(a => a.code?.toString() === defaultValue?.toString());
          if (matchingAccount) {
            (newDefaults as any)[key] = defaultValue;
            changed = true;
          }
        }
      });

      if (changed) {
        setSettings(prev => ({
          ...prev,
          defaultAccounts: newDefaults
        }));
      }
    }
  }, [accounts, settings.defaultAccounts]);

  const [selectedPreviewTheme, setSelectedPreviewTheme] = useState<BrandingTheme | null>(null);
  const [selectedPreviewModule, setSelectedPreviewModule] = useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedLayoutModule, setSelectedLayoutModule] = useState<'general' | 'quotation' | 'invoice' | 'delivery' | 'purchaseOrder' | 'bill'>('general');

  // Normalize theme docs fetched from Firestore/API so legacy/custom records missing
  // optional fields (e.g. fontFamily) never crash render call sites.
  const normalizeBrandingTheme = (theme: any): BrandingTheme => ({
    id: String(theme?.id ?? ''),
    name: String(theme?.name ?? ''),
    logoUrl: String(theme?.logoUrl ?? ''),
    primaryColor: String(theme?.primaryColor ?? '#000000'),
    fontFamily: String(theme?.fontFamily ?? ''),
    invoiceFooterText: String(theme?.invoiceFooterText ?? ''),
    organizationDetails: String(theme?.organizationDetails ?? ''),
    logoOverrideUrl: theme?.logoOverrideUrl ? String(theme.logoOverrideUrl) : undefined,
    logoSize: theme?.logoSize ?? 'Medium',
    logoAlignment: theme?.logoAlignment ?? 'Right',
    infoAlignment: theme?.infoAlignment ?? 'Left',
    createdAt: theme?.createdAt ?? null,
    updatedAt: theme?.updatedAt ?? null,
  });

  const fetchBrandingThemes = () => {
    if (!orgId) return;

    const token = getLocalAuthToken();
    if (token) {
      let cancelled = false;
      void fetch(`/api/organizations/${encodeURIComponent(orgId)}/branding-themes`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(async response => {
          if (!response.ok) {
            throw new Error(`Failed to load branding themes: ${response.status}`);
          }
          return response.json();
        })
        .then(themes => {
          if (!cancelled) {
            setBrandingThemes(Array.isArray(themes) ? themes.map(normalizeBrandingTheme) : []);
          }
        })
        .catch(error => {
          if (!cancelled) {
            console.error('Local API error loading branding themes:', error);
            notify.error('Failed to load branding themes');
          }
        });

      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'branding_themes'), where('orgId', '==', orgId));
    return onSnapshot(q, (snapshot) => {
      setBrandingThemes(snapshot.docs.map(doc => normalizeBrandingTheme({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'branding_themes');
    });
  };

  const handleSaveTheme = async () => {
    if (!newTheme.name.trim()) {
      notify.error('Theme Name is required');
      return;
    }

    const token = getLocalAuthToken();
    setSaving(true);
    try {
      if (token) {
        const endpoint = editingTheme
          ? `/api/organizations/${encodeURIComponent(orgId)}/branding-themes/${encodeURIComponent(editingTheme.id)}`
          : `/api/organizations/${encodeURIComponent(orgId)}/branding-themes`;
        const response = await fetch(endpoint, {
          method: editingTheme ? 'PATCH' : 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newTheme)
        });

        if (!response.ok) {
          throw new Error(`Failed to save branding theme: ${response.status}`);
        }

        void fetchBrandingThemes();
        notify.confirm(editingTheme ? 'Branding theme updated successfully' : 'Branding theme created successfully');
      } else if (editingTheme) {
        await updateDoc(doc(db, 'branding_themes', editingTheme.id), {
          ...newTheme,
          updatedAt: serverTimestamp()
        });
        notify.confirm('Branding theme updated successfully');
      } else {
        await addDoc(collection(db, 'branding_themes'), {
          ...newTheme,
          orgId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        notify.confirm('Branding theme created successfully');
      }
      setIsThemeModalOpen(false);
      setEditingTheme(null);
      setNewTheme({
        name: '',
        logoUrl: '',
        primaryColor: '#000000',
        fontFamily: 'Helvetica, Arial, sans-serif',
        invoiceFooterText: '',
        organizationDetails: '',
        logoOverrideUrl: '',
        logoSize: 'Medium',
        logoAlignment: 'Right',
        infoAlignment: 'Left',
        showBankDetails: false,
        bankAccounts: [],
        paymentInstructions: '',
        enableWatermark: false,
        watermarkType: 'Text Only',
        watermarkOpacity: 8,
        autoStatusText: true
      });
    } catch (error) {
      if (token) {
        console.error('Local API error saving branding theme:', error);
      } else {
        handleFirestoreError(error, OperationType.WRITE, 'branding_themes');
      }
      notify.error('Failed to save branding theme');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTheme = async (themeId: string) => {
    if (!window.confirm('Are you sure you want to delete this branding theme?')) return;

    const token = getLocalAuthToken();
    try {
      if (token) {
        const response = await fetch(
          `/api/organizations/${encodeURIComponent(orgId)}/branding-themes/${encodeURIComponent(themeId)}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to delete branding theme: ${response.status}`);
        }

        void fetchBrandingThemes();
      } else {
        await deleteDoc(doc(db, 'branding_themes', themeId));
      }
      notify.confirm('Branding theme deleted');
    } catch (error) {
      if (token) {
        console.error('Local API error deleting branding theme:', error);
      } else {
        handleFirestoreError(error, OperationType.DELETE, `branding_themes/${themeId}`);
      }
      notify.error('Failed to delete branding theme');
    }
  };

  const fetchStaff = () => {
    if (!orgId) return;
    if (getLocalAuthToken()) {
      setStaffList([]);
      return;
    }
    const q = query(collection(db, 'org_users'));
    return onSnapshot(q, (snapshot) => {
      const allUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as OrgUser));
      // Filter users who have membership in this org
      const filteredStaff = allUsers.filter(user => 
        user.memberships?.some(m => m.orgId === orgId)
      );
      setStaffList(filteredStaff);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'org_users');
    });
  };

  const handleSaveStaff = async () => {
    if (getLocalAuthToken()) {
      notify.confirm('Staff provisioning is not migrated to local PostgreSQL yet.');
      return;
    }
    if (!newStaff.email.trim() || !newStaff.fullName.trim()) {
      notify.error('Full Name and Email are required');
      return;
    }

    setSaving(true);
    try {
      // Check if user already exists
      const q = query(collection(db, 'org_users'), where('email', '==', newStaff.email.trim()));
      const querySnapshot = await getDocs(q);
      
      const membership: OrgMembership = {
        orgId,
        role: newStaff.role,
        permissions: newStaff.permissions
      };

      if (!querySnapshot.empty) {
        // User exists, update memberships
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data() as OrgUser;
        const existingMemberships = userData.memberships || [];
        
        // Remove existing membership for this org if it exists
        const otherMemberships = existingMemberships.filter(m => m.orgId !== orgId);
        const updatedMemberships = [...otherMemberships, membership];

        await updateDoc(doc(db, 'org_users', userDoc.id), {
          fullName: newStaff.fullName, // Update name if provided
          memberships: updatedMemberships,
          updatedAt: serverTimestamp()
        });
        notify.confirm('Staff updated successfully');
      } else {
        // New user
        await addDoc(collection(db, 'org_users'), {
          fullName: newStaff.fullName,
          email: newStaff.email.trim(),
          memberships: [membership],
          suspended: false,
          createdAt: serverTimestamp()
        });
        notify.confirm('Staff added successfully');
      }

      setIsStaffModalOpen(false);
      setEditingStaff(null);
      setNewStaff({
        fullName: '',
        email: '',
        role: 'user',
        permissions: ['dashboard']
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'org_users');
      notify.error('Failed to save staff');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStaff = async (staffId: string) => {
    if (!window.confirm('Are you sure you want to remove this staff member from your organization?')) return;
    
    try {
      const staffDoc = doc(db, 'org_users', staffId);
      const docSnap = await getDoc(staffDoc);
      
      if (docSnap.exists()) {
        const userData = docSnap.data() as OrgUser;
        const updatedMemberships = userData.memberships.filter(m => m.orgId !== orgId);
        
        if (updatedMemberships.length === 0) {
          // If no memberships left, we could delete the user or just keep them with no memberships
          // For now, let's just remove the membership
          await updateDoc(staffDoc, {
            memberships: [],
            updatedAt: serverTimestamp()
          });
        } else {
          await updateDoc(staffDoc, {
            memberships: updatedMemberships,
            updatedAt: serverTimestamp()
          });
        }
        notify.confirm('Staff removed from organization');
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `org_users/${staffId}`);
      notify.error('Failed to remove staff');
    }
  };

  useEffect(() => {
    if (settings.allowedCurrencies.length > 0) {
      fetchExchangeRates();
      const interval = setInterval(fetchExchangeRates, 15 * 60 * 1000); // 15 minutes
      return () => clearInterval(interval);
    }
  }, [settings.allowedCurrencies, settings.defaultCurrency]);

  const fetchExchangeRates = async () => {
    if (settings.allowedCurrencies.length === 0) return;
    
    setFetchingRates(true);
    try {
      // Using a reliable free API for structured data
      // We'll call it "Live Rates" as requested, often matching Google Finance
      const response = await fetch(`https://open.er-api.com/v6/latest/${settings.defaultCurrency}`);
      const data = await response.json();
      
      if (data.result === 'success') {
        const rates: Record<string, number> = {};
        settings.allowedCurrencies.forEach(curr => {
          if (data.rates[curr]) {
            rates[curr] = data.rates[curr];
          }
        });
        setExchangeRates(rates);
        setLastRatesUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
    } finally {
      setFetchingRates(false);
    }
  };

  const seedBasicAccounts = async () => {
    if (!orgId || !auth.currentUser) return;
    setSaving(true);
    try {
      const { CORE_CHART_OF_ACCOUNTS } = await import('../constants');
      
      const basicAccounts = CORE_CHART_OF_ACCOUNTS.map(acc => ({
        ...acc,
        isSystemAccount: true,
        status: 'active' as const
      }));

      const path = 'chart_of_accounts';
      const userName = auth.currentUser.displayName || auth.currentUser.email?.split('@')[0] || 'System';
      const userEmail = auth.currentUser.email || 'system@accountex.com';

      // Fetch existing accounts to avoid double seeding and to get IDs for mapping
      const existingQ = query(collection(db, path), where('orgId', '==', orgId));
      const existingSnap = await getDocs(existingQ);
      const accountsByCode: Record<string, string> = {};
      existingSnap.docs.forEach(d => {
        accountsByCode[d.data().code] = d.id;
      });

      for (const acc of basicAccounts) {
        if (!accountsByCode[acc.code]) {
          const docRef = await addDoc(collection(db, path), {
            ...acc,
            orgId,
            createdBy: auth.currentUser.uid,
            createdByName: userName,
            createdByEmail: userEmail,
            createdAt: serverTimestamp(),
            actionLog: [{ type: 'create', by: 'System Seed (Settings)', at: new Date().toISOString() }]
          });
          accountsByCode[acc.code] = docRef.id;
          
          // Also handle bank accounts creation if needed
          if (acc.type === 'Bank') {
            const bq = query(collection(db, 'bank_accounts'), where('orgId', '==', orgId), where('accountNumber', '==', acc.code));
            const bsnap = await getDocs(bq);
            if (bsnap.empty) {
              await addDoc(collection(db, 'bank_accounts'), {
                accountName: acc.name,
                accountNumber: acc.code,
                bankName: 'System Bank',
                balance: 0,
                currency: getDefaultCurrency(settings),
                orgId,
                status: 'active',
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp()
              });
            }
          }
        }
      }

      // Automatically map default accounts in settings
      const mapping: Record<string, string> = {
        'salesAccount': '4010',
        'accountsReceivable': '1100',
        'accountsPayable': '2010',
        'inventoryAccount': '1200',
        'cogsAccount': '5010',
        'vatInput': '1400',
        'vatOutput': '2100',
        'vatPayable': '2110',
        'bankCash': '1020',
        'purchaseAccount': '5020',
        'salesReturns': '4040',
        'purchaseReturns': '5030',
        'discountGiven': '4030',
        'discountReceived': '6140'
      };

      const updatedDefaultAccounts = { ...(settings.defaultAccounts || {}) };
      let changed = false;

      Object.entries(mapping).forEach(([key, code]) => {
        if (!updatedDefaultAccounts[key as keyof typeof updatedDefaultAccounts] && accountsByCode[code]) {
          (updatedDefaultAccounts as any)[key] = accountsByCode[code];
          changed = true;
        }
      });

      if (changed) {
        const newSettings = { ...settings, defaultAccounts: updatedDefaultAccounts };
        setSettings(newSettings);
        await setDoc(doc(db, 'settings', orgId), {
          ...newSettings,
          orgId,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || 'system'
        });
      }

      notify.confirm("Core Chart of Accounts initialized and default accounts linked");
    } catch (err) {
      console.error("Error seeding accounts in settings:", err);
      notify.error("Failed to seed accounts");
    } finally {
      setSaving(false);
    }
  };

  const getLocalAuthToken = () => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('app_auth_token');
  };

  const normalizeSettings = (storedSettings: Partial<OrganizationSettings>) => {
    const storedAccounts = storedSettings.defaultAccounts || {};
    const cleanedStoredAccounts = Object.entries(storedAccounts).reduce((acc: any, [key, val]) => {
      if (val && val !== '') {
        acc[key] = val;
      }
      return acc;
    }, {});

    return {
      ...DEFAULT_SETTINGS,
      ...storedSettings,
      phoneNumbers: Array.isArray(storedSettings.phoneNumbers) ? storedSettings.phoneNumbers : (DEFAULT_SETTINGS.phoneNumbers || []),
      allowedCurrencies: Array.isArray(storedSettings.allowedCurrencies) ? storedSettings.allowedCurrencies : (DEFAULT_SETTINGS.allowedCurrencies || []),
      defaultAccounts: {
        ...DEFAULT_SETTINGS.defaultAccounts,
        ...cleanedStoredAccounts
      }
    } as OrganizationSettings;
  };

  const saveSettings = async (settingsToSave: OrganizationSettings) => {
    const token = getLocalAuthToken();

    if (token) {
      const {
        defaultCurrency,
        defaultPaymentTerms,
        allowedCurrencies,
        defaultAccounts,
        payroll,
        ...data
      } = settingsToSave;
      const response = await fetch(`/api/organizations/${orgId}/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data,
          defaultCurrency,
          defaultPaymentTerms,
          allowedCurrencies,
          defaultAccounts,
          payroll
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save organization settings (${response.status})`);
      }
      return;
    }

    const docRef = doc(db, 'settings', orgId);
    await setDoc(docRef, {
      ...settingsToSave,
      orgId,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || 'system'
    });
  };

  const fetchSettings = async () => {
    if (!orgId) return;
    try {
      const token = getLocalAuthToken();
      if (token) {
        const response = await fetch(`/api/organizations/${orgId}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error(`Failed to load organization settings (${response.status})`);
        }

        const payload = await response.json();
        const data = payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
          ? payload.data
          : {};
        setSettings(normalizeSettings({
          ...data,
          // PostgreSQL responds with snake_case columns. Prefer those top-level
          // values over legacy copies that may still exist inside `data`.
          defaultCurrency: payload.defaultCurrency ?? payload.default_currency ?? data.defaultCurrency ?? data.default_currency,
          defaultPaymentTerms: payload.defaultPaymentTerms ?? payload.default_payment_terms ?? data.defaultPaymentTerms ?? data.default_payment_terms,
          allowedCurrencies: payload.allowedCurrencies ?? payload.allowed_currencies ?? data.allowedCurrencies ?? data.allowed_currencies,
          defaultAccounts: payload.defaultAccounts ?? payload.default_accounts ?? data.defaultAccounts ?? data.default_accounts,
          payroll: payload.payroll ?? data.payroll,
          invoicePrefix: payload.invoicePrefix ?? payload.invoice_prefix ?? data.invoicePrefix,
          nextInvoiceNumber: Number(payload.nextInvoiceNumber ?? payload.next_invoice_number ?? data.nextInvoiceNumber) || 1,
          creditNotePrefix: payload.creditNotePrefix ?? data.creditNotePrefix,
          nextCreditNoteNumber: Number(payload.nextCreditNoteNumber ?? data.nextCreditNoteNumber) || 1
        }));
        return;
      }

      const docRef = doc(db, 'settings', orgId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSettings(normalizeSettings(data));
      } else {
        setSettings(DEFAULT_SETTINGS);
      }

      // Fetch Catalog Settings from Organization doc
      const orgRef = doc(db, 'organizations', orgId);
      const orgSnap = await getDoc(orgRef);
      if (orgSnap.exists()) {
        const orgData = orgSnap.data();
        setOrganization({ id: orgSnap.id, ...orgData });
        
        // Sync vital fields from organization doc if they exist
        setSettings(prev => ({
          ...prev,
          companyName: orgData.companyName || orgData.name || prev.companyName,
          legalName: orgData.legalName || prev.legalName,
          industry: orgData.industry || prev.industry,
          country: orgData.country || prev.country,
          city: orgData.city || prev.city,
          mailingAddress: orgData.mailingAddress || prev.mailingAddress,
          email: orgData.email || prev.email,
          website: orgData.website || prev.website,
          logoUrl: orgData.logoUrl || prev.logoUrl
        }));

        if (orgData.catalogSettings) {
          setCatalogSettings(prev => ({ ...prev, ...orgData.catalogSettings }));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      notify.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      if (activeTab === 'organization' || activeTab === 'defaults') {
        await saveSettings(settings);

        // Keep the organization document in sync for Firebase sessions.
        if (!getLocalAuthToken()) {
          const orgRef = doc(db, 'organizations', orgId);
          await updateDoc(orgRef, {
            name: settings.companyName || '',
            companyName: settings.companyName || '',
            legalName: settings.legalName || '',
            logoUrl: settings.logoUrl || '',
            industry: settings.industry || '',
            businessStructure: settings.businessStructure || '',
            registrationNumber: settings.registrationNumber || '',
            taxNumber: settings.taxNumber || '',
            description: settings.description || '',
            mailingAddress: settings.mailingAddress || '',
            country: settings.country || '',
            city: settings.city || '',
            attention: settings.attention || '',
            phoneNumbers: settings.phoneNumbers || [],
            fax: settings.fax || { code: '', number: '' },
            email: settings.email || '',
            website: settings.website || '',
            updatedAt: serverTimestamp()
          });
        }
      } else if (activeTab === 'catalog') {
        if (getLocalAuthToken()) {
          notify.confirm('Catalog settings are not migrated to local PostgreSQL yet.');
          return;
        }
        const orgRef = doc(db, 'organizations', orgId);
        await updateDoc(orgRef, {
          catalogSettings,
          updatedAt: serverTimestamp()
        });
      }
      notify.confirm('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      if (getLocalAuthToken()) {
        notify.error('Failed to save settings');
      } else {
        handleFirestoreError(error, OperationType.WRITE, `settings/${orgId}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const removeCurrency = (curr: string) => {
    setSettings({
      ...settings,
      allowedCurrencies: settings.allowedCurrencies.filter(c => c !== curr)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <SettingsIcon className="w-8 h-8" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your organization profile and staff access.
          </p>
        </div>
        {(activeTab === 'organization' || activeTab === 'defaults' || activeTab === 'catalog') && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
        {activeTab === 'staff' && (
          <button
            onClick={() => {
              setEditingStaff(null);
              setNewStaff({
                fullName: '',
                email: '',
                role: 'user',
                permissions: ['dashboard']
              });
              setIsStaffModalOpen(true);
            }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <UserPlus className="w-4 h-4" />
            Add Staff
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 sm:gap-4 border-b overflow-x-auto whitespace-nowrap no-scrollbar pb-1">
        <button
          onClick={() => setActiveTab('organization')}
          className={cn(
            "px-4 py-2 text-sm font-bold transition-all border-b-2",
            activeTab === 'organization' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Organization
        </button>
        <button
          onClick={() => setActiveTab('defaults')}
          className={cn(
            "px-4 py-2 text-sm font-bold transition-all border-b-2",
            activeTab === 'defaults' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Default Accounts
        </button>
        <button
          onClick={() => setActiveTab('invoice-layouts')}
          className={cn(
            "px-4 py-2 text-sm font-bold transition-all border-b-2",
            activeTab === 'invoice-layouts' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Invoice Layouts
        </button>
        <button
          onClick={() => setActiveTab('staff')}
          className={cn(
            "px-4 py-2 text-sm font-bold transition-all border-b-2",
            activeTab === 'staff' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Staff
        </button>
        <button
          onClick={() => setActiveTab('catalog')}
          className={cn(
            "px-4 py-2 text-sm font-bold transition-all border-b-2",
            activeTab === 'catalog' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Catalog Landing Page
        </button>
        <button
          onClick={() => setActiveTab('access-rights')}
          className={cn(
            "px-4 py-2 text-sm font-bold transition-all border-b-2",
            activeTab === 'access-rights' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Access Rights
        </button>
      </div>

      {activeTab === 'organization' ? (
        <div key="org-tab" className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Basic Information */}
            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="p-6 border-b bg-muted/30">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  Basic Information
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  General details about your organization
                </p>
              </div>
              <div className="p-6 space-y-6">
                {/* Logo Upload */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Organization Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 border rounded-lg overflow-hidden bg-muted flex items-center justify-center relative group">
                      {settings.logoUrl ? (
                        <img src={settings.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                      ) : (
                        <Upload className="w-8 h-8 text-muted-foreground" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            notify.error('Logo file size must be less than 5MB');
                            return;
                          }
                          setSaving(true);
                          try {
                            const storageRef = ref(storage, `organizations/${orgId}/logo_${Date.now()}`);
                            const snapshot = await uploadBytes(storageRef, file);
                            const url = await getDownloadURL(snapshot.ref);
                            setSettings({ ...settings, logoUrl: url });
                            notify.confirm('Logo uploaded successfully');
                          } catch (error) {
                            console.error('Logo upload error:', error);
                            notify.error('Failed to upload logo');
                          } finally {
                            setSaving(false);
                          }
                        }}
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-xs font-medium">Click box to upload logo</p>
                      <p className="text-[10px] text-muted-foreground">Recommended: Square, PNG or JPG (Max 5MB)</p>
                      {settings.logoUrl && (
                        <button 
                          onClick={() => setSettings({ ...settings, logoUrl: '' })}
                          className="text-[10px] text-destructive hover:underline"
                        >
                          Remove Logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Display Name *</label>
                    <input
                      type="text"
                      value={settings.companyName || ''}
                      onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="Organization Display Name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Legal Name</label>
                    <input
                      type="text"
                      value={settings.legalName || ''}
                      onChange={(e) => setSettings({ ...settings, legalName: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="Legal Entity Name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Industry</label>
                    <input
                      type="text"
                      value={settings.industry || ''}
                      onChange={(e) => setSettings({ ...settings, industry: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="e.g. Technology, Retail"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Business Structure</label>
                    <select
                      value={settings.businessStructure || ''}
                      onChange={(e) => setSettings({ ...settings, businessStructure: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    >
                      <option value="">Select Structure...</option>
                      <option value="Sole Proprietor">Sole Proprietor</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Corporation">Corporation</option>
                      <option value="Non-Profit">Non-Profit</option>
                      <option value="Trust">Trust</option>
                      <option value="LLC">Limited Liability Company (LLC)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Business Reg. Number</label>
                    <input
                      type="text"
                      value={settings.registrationNumber || ''}
                      onChange={(e) => setSettings({ ...settings, registrationNumber: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="Reg # / Tax ID"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Tax Number (VAT/GST)</label>
                    <input
                      type="text"
                      value={settings.taxNumber || ''}
                      onChange={(e) => setSettings({ ...settings, taxNumber: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="Tax Registration #"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Organization Description</label>
                  <textarea
                    value={settings.description || ''}
                    onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none min-h-[100px]"
                    placeholder="Brief description of your business..."
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="p-6 border-b bg-muted/30">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  Contact Information
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  How customers and staff can reach you
                </p>
              </div>
              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Mailing Address</label>
                  <textarea
                    value={settings.mailingAddress || ''}
                    onChange={(e) => setSettings({ ...settings, mailingAddress: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none min-h-[80px]"
                    placeholder="Full business address..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Country</label>
                    <select
                      value={settings.country || ''}
                      onChange={(e) => setSettings({ ...settings, country: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    >
                      <option value="">Select Country...</option>
                      {COUNTRIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">City</label>
                    {settings.country && (CITY_MAP as any)[settings.country] ? (
                      <select
                        value={settings.city || ''}
                        onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        <option value="">Select City...</option>
                        {((CITY_MAP as any)[settings.country] || []).map((city: string) => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={settings.city || ''}
                        onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                        placeholder="Enter City"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Attention Message</label>
                    <input
                      type="text"
                      value={settings.attention || ''}
                      onChange={(e) => setSettings({ ...settings, attention: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="e.g. Accounts Payable Department"
                    />
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Phone Numbers</label>
                    <button
                      type="button"
                      onClick={() => setSettings({
                        ...settings,
                        phoneNumbers: [...(settings.phoneNumbers || []), { countryCode: '+20', number: '' }]
                      })}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Phone
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(settings.phoneNumbers || [{ countryCode: '+20', number: '' }]).map((phone, index) => (
                      <div key={index} className="flex gap-2">
                        <select
                          value={phone.countryCode}
                          onChange={(e) => {
                            const newPhones = [...(settings.phoneNumbers || [])];
                            if (newPhones.length === 0) newPhones.push({ countryCode: '+20', number: '' });
                            newPhones[index].countryCode = e.target.value;
                            setSettings({ ...settings, phoneNumbers: newPhones });
                          }}
                          className="w-40 px-2 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                        >
                          {COUNTRY_CODES.map(c => (
                            <option key={`${c.code}-${c.country}`} value={c.code}>{c.country} ({c.code})</option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          value={phone.number}
                          onChange={(e) => {
                            const newPhones = [...(settings.phoneNumbers || [])];
                            if (newPhones.length === 0) newPhones.push({ countryCode: '+20', number: '' });
                            newPhones[index].number = e.target.value;
                            setSettings({ ...settings, phoneNumbers: newPhones });
                          }}
                          className="flex-1 px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                          placeholder="Phone Number"
                        />
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newPhones = (settings.phoneNumbers || []).filter((_, i) => i !== index);
                              setSettings({ ...settings, phoneNumbers: newPhones });
                            }}
                            className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <label className="text-sm font-medium">Fax</label>
                  <div className="flex gap-2">
                    <select
                      value={settings.fax?.code || '+20'}
                      onChange={(e) => setSettings({
                        ...settings,
                        fax: { ...(settings.fax || { code: '+20', number: '' }), code: e.target.value }
                      })}
                      className="w-40 px-2 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                    >
                      {COUNTRY_CODES.map(c => (
                        <option key={`${c.code}-${c.country}-fax`} value={c.code}>{c.country} ({c.code})</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={settings.fax?.number || ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        fax: { ...(settings.fax || { code: '+20', number: '' }), number: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                      placeholder="Fax Number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email Address</label>
                    <input
                      type="email"
                      value={settings.email || ''}
                      onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="contact@business.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Website</label>
                    <input
                      type="url"
                      value={settings.website || ''}
                      onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="https://www.business.com"
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Social Media Profiles</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase opacity-50">Facebook</label>
                      <input
                        type="text"
                        value={settings.facebook || ''}
                        onChange={(e) => setSettings({ ...settings, facebook: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                        placeholder="facebook.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase opacity-50">Twitter / X</label>
                      <input
                        type="text"
                        value={settings.twitter || ''}
                        onChange={(e) => setSettings({ ...settings, twitter: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                        placeholder="@username"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase opacity-50">LinkedIn</label>
                      <input
                        type="text"
                        value={settings.linkedIn || ''}
                        onChange={(e) => setSettings({ ...settings, linkedIn: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                        placeholder="linkedin.com/company/..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Currency Settings */}
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="p-6 border-b bg-muted/30 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  Currency Settings
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure default currency and allowed currencies for your organization
                </p>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-1">
                  Default Currency *
                  <Info className="w-3 h-3 text-muted-foreground" />
                </label>
                <select
                  value={settings.defaultCurrency}
                  onChange={(e) => setSettings({ ...settings, defaultCurrency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                >
                  {WORLD_CURRENCIES.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.name} ({curr.code})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">Primary currency for your organization (Only one allowed)</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Default Payment Terms (Days)</label>
                <input
                  type="number"
                  value={settings.defaultPaymentTerms}
                  onChange={(e) => setSettings({ ...settings, defaultPaymentTerms: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                />
                <p className="text-xs text-muted-foreground">Number of days for payment terms</p>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Allowed Currencies</label>
                  <div className="flex gap-2">
                    <select
                      value={newCurrency}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        if (settings.allowedCurrencies.includes(val)) {
                          notify.error('Currency already added');
                          setNewCurrency('');
                          return;
                        }
                        setSettings({
                          ...settings,
                          allowedCurrencies: [...settings.allowedCurrencies, val]
                        });
                        setNewCurrency('');
                      }}
                      className="flex-1 px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    >
                      <option value="">Select a currency to add...</option>
                      {WORLD_CURRENCIES.map(curr => (
                        <option key={curr.code} value={curr.code}>
                          {curr.name} ({curr.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-muted-foreground">Select one or more currencies allowed for your organization</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {(settings.allowedCurrencies || []).map((curr) => (
                    <span
                      key={curr}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-md border border-primary/20"
                    >
                      {curr}
                      <button
                        onClick={() => removeCurrency(curr)}
                        className="hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>

                {/* Exchange Rates Section */}
                {(settings.allowedCurrencies || []).length > 0 && (
                  <div className="pt-4 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        Live Exchange Rates
                      </h3>
                      <div className="flex items-center gap-2">
                        {lastRatesUpdate && (
                          <span className="text-[10px] text-muted-foreground">
                            Updated: {lastRatesUpdate.toLocaleTimeString()}
                          </span>
                        )}
                        <button
                          onClick={fetchExchangeRates}
                          disabled={fetchingRates}
                          className={cn(
                            "p-1 hover:bg-muted rounded-md transition-colors",
                            fetchingRates && "animate-spin"
                          )}
                          title="Refresh rates"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {(settings.allowedCurrencies || []).map(curr => (
                        <div key={curr} className="p-3 bg-muted/30 rounded-lg border border-border/50 flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                            1 {settings.defaultCurrency} =
                          </span>
                          <div className="flex items-baseline gap-1">
                            <span className="text-lg font-bold">
                              {exchangeRates[curr] ? exchangeRates[curr].toFixed(4) : '...'}
                            </span>
                            <span className="text-xs font-medium text-muted-foreground">{curr}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground italic">
                      Rates are updated automatically every 15 minutes.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Sales Invoice Settings */}
            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="p-6 border-b bg-muted/30 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Sales Invoice Settings
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Customize sales invoice numbering (assigned when invoice is approved)
                  </p>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center justify-between">
                      Quote Prefix *
                    </label>
                    <input
                      type="text"
                      value={settings.quotePrefix}
                      onChange={(e) => setSettings({ ...settings, quotePrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="QT-"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center justify-between">
                      Next Quote Number *
                      <button 
                        onClick={async () => {
                          const newSettings = { ...settings, nextQuoteNumber: 1 };
                          setSettings(newSettings);
                          try {
                            await saveSettings(newSettings);
                            notify.confirm('Quote number reset and saved');
                          } catch (err) {
                            if (getLocalAuthToken()) {
                              console.error('Error saving settings:', err);
                              notify.error('Failed to save settings');
                            } else {
                              handleFirestoreError(err, OperationType.WRITE, `settings/${orgId}`);
                            }
                          }
                        }}
                        className="text-[10px] uppercase tracking-wider font-bold text-primary hover:underline"
                      >
                        Reset to 1
                      </button>
                    </label>
                    <input
                      type="number"
                      value={settings.nextQuoteNumber}
                      onChange={(e) => setSettings({ ...settings, nextQuoteNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center justify-between">
                      Invoice Prefix *
                    </label>
                    <input
                      type="text"
                      value={settings.invoicePrefix}
                      onChange={(e) => setSettings({ ...settings, invoicePrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="INV-"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center justify-between">
                      Next Number *
                      <button 
                        onClick={async () => {
                          const newSettings = { ...settings, nextInvoiceNumber: 1 };
                          setSettings(newSettings);
                          try {
                            await saveSettings(newSettings);
                            notify.confirm('Invoice number reset and saved');
                          } catch (err) {
                            if (getLocalAuthToken()) {
                              console.error('Error saving settings:', err);
                              notify.error('Failed to save settings');
                            } else {
                              handleFirestoreError(err, OperationType.WRITE, `settings/${orgId}`);
                            }
                          }
                        }}
                        className="text-[10px] uppercase tracking-wider font-bold text-primary hover:underline"
                      >
                        Reset to 1
                      </button>
                    </label>
                    <input
                      type="number"
                      value={settings.nextInvoiceNumber}
                      onChange={(e) => setSettings({ ...settings, nextInvoiceNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Credit Note Prefix *</label>
                    <input
                      type="text"
                      value={settings.creditNotePrefix}
                      onChange={(e) => setSettings({ ...settings, creditNotePrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="CN-"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Next Number *</label>
                    <input
                      type="number"
                      value={settings.nextCreditNoteNumber}
                      onChange={(e) => setSettings({ ...settings, nextCreditNoteNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <label className="text-sm font-medium">Default Branding Theme</label>
                  <select
                    value={settings.defaultInvoiceBrandingTheme}
                    onChange={(e) => setSettings({ ...settings, defaultInvoiceBrandingTheme: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="Standard">Standard</option>
                    <option value="Professional">Professional</option>
                    <option value="Modern">Modern</option>
                    <option value="Minimal">Minimal</option>
                    {brandingThemes.map(theme => (
                      <option key={theme.id} value={theme.id}>{theme.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Purchase Settings */}
            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="p-6 border-b bg-muted/30 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-primary" />
                    Purchase Settings
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Customize purchase order and bill numbering
                  </p>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">PO Prefix *</label>
                    <input
                      type="text"
                      value={settings.purchaseOrderPrefix}
                      onChange={(e) => setSettings({ ...settings, purchaseOrderPrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="PO-"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Next Number *</label>
                    <input
                      type="number"
                      value={settings.nextPurchaseOrderNumber}
                      onChange={(e) => setSettings({ ...settings, nextPurchaseOrderNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Bill Prefix *</label>
                    <input
                      type="text"
                      value={settings.purchaseBillPrefix}
                      onChange={(e) => setSettings({ ...settings, purchaseBillPrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="BILL-"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Next Number *</label>
                    <input
                      type="number"
                      value={settings.nextPurchaseBillNumber}
                      onChange={(e) => setSettings({ ...settings, nextPurchaseBillNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <label className="text-sm font-medium">Default Branding Theme</label>
                  <select
                    value={settings.defaultBillBrandingTheme}
                    onChange={(e) => setSettings({ ...settings, defaultBillBrandingTheme: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="Standard">Standard</option>
                    <option value="Professional">Professional</option>
                    <option value="Modern">Modern</option>
                    <option value="Minimal">Minimal</option>
                    {brandingThemes.map(theme => (
                      <option key={theme.id} value={theme.id}>{theme.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="p-6 border-b bg-muted/30 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Additional Prefixes
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage additional transaction numbering prefixes
                  </p>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Fixed Assets Prefix *</label>
                    <input
                      type="text"
                      value={settings.fixedAssetsPrefix}
                      onChange={(e) => setSettings({ ...settings, fixedAssetsPrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="FA-"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center justify-between">
                      Next Number *
                      <button 
                        onClick={async () => {
                          const newSettings = { ...settings, nextFixedAssetsNumber: 1 };
                          setSettings(newSettings);
                          try {
                            await saveSettings(newSettings);
                            notify.confirm('Fixed Assets number reset and saved');
                          } catch (err) {
                            if (getLocalAuthToken()) {
                              console.error('Error saving settings:', err);
                              notify.error('Failed to save settings');
                            } else {
                              handleFirestoreError(err, OperationType.WRITE, `settings/${orgId}`);
                            }
                          }
                        }}
                        className="text-[10px] uppercase tracking-wider font-bold text-primary hover:underline"
                      >
                        Reset to 1
                      </button>
                    </label>
                    <input
                      type="number"
                      value={settings.nextFixedAssetsNumber}
                      onChange={(e) => setSettings({ ...settings, nextFixedAssetsNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Manual Journal Prefix *</label>
                    <input
                      type="text"
                      value={settings.manualJournalPrefix}
                      onChange={(e) => setSettings({ ...settings, manualJournalPrefix: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="MJ-"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Next Number *</label>
                    <input
                      type="number"
                      value={settings.nextManualJournalNumber}
                      onChange={(e) => setSettings({ ...settings, nextManualJournalNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            </div>
          </div>
        </div>
      ) : activeTab === 'defaults' ? (
        <div className="max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="p-6 border-b bg-muted/30">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Default Accounts
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Map system processes to specific accounts in your Chart of Accounts
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { key: 'salesAccount', label: 'Sales Revenue Account' },
                  { key: 'salesReturns', label: 'Sales Returns Account' },
                  { key: 'accountsReceivable', label: 'Accounts Receivable' },
                  { key: 'accountsPayable', label: 'Accounts Payable' },
                  { key: 'inventoryAccount', label: 'Inventory Account' },
                  { key: 'cogsAccount', label: 'COGS Account' },
                  { key: 'vatInput', label: 'VAT Input Account' },
                  { key: 'vatOutput', label: 'VAT Output Account' },
                  { key: 'vatPayable', label: 'VAT Settlement Account' },
                  { key: 'bankCash', label: 'Default Bank/Cash Account' },
                  { key: 'purchaseAccount', label: 'Purchase Account' },
                  { key: 'purchaseReturns', label: 'Purchase Returns Account' },
                  { key: 'discountGiven', label: 'Discount Given Account' },
                  { key: 'discountReceived', label: 'Discount Received Account' }
                ].map((field) => (
                  <div key={field.key} className="space-y-2">
                    <label className="text-[10px] font-bold uppercase opacity-50">{field.label}</label>
                    <select
                      value={(settings.defaultAccounts as any)?.[field.key]?.toString() || ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        defaultAccounts: {
                          ...(settings.defaultAccounts || {}),
                          [field.key]: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                    >
                      <option value="">Select Account...</option>
                      {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'invoice-layouts' ? (
        <div className="max-w-6xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="md:col-span-1 space-y-1">
              <button
                onClick={() => setSelectedLayoutModule('general')}
                className={cn(
                  "w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between group",
                  selectedLayoutModule === 'general' ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "hover:bg-muted"
                )}
              >
                <div className="flex items-center gap-3">
                  <Library className="w-4 h-4" />
                  <span className="text-sm font-bold">Theme Library</span>
                </div>
                <ChevronRight className={cn("w-4 h-4 transition-transform", selectedLayoutModule === 'general' ? "rotate-90" : "opacity-0 group-hover:opacity-50")} />
              </button>
              
              <div className="pt-4 pb-2 px-4">
                <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Module Layouts</span>
              </div>
              
              {[
                { id: 'quotation', name: 'Quotation', icon: FileText },
                { id: 'invoice', name: 'Sales Invoice', icon: Sparkles },
                { id: 'delivery', name: 'Delivery Note', icon: Truck },
                { id: 'purchaseOrder', name: 'Purchase Order', icon: ShoppingBag },
                { id: 'bill', name: 'Bill', icon: Receipt }
              ].map((mod) => {
                const Icon = mod.icon;
                return (
                  <button
                    key={mod.id}
                    onClick={() => setSelectedLayoutModule(mod.id as any)}
                    className={cn(
                      "w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between group",
                      selectedLayoutModule === mod.id ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "hover:bg-muted"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-bold">{mod.name}</span>
                    </div>
                    <ChevronRight className={cn("w-4 h-4 transition-transform", selectedLayoutModule === mod.id ? "rotate-90" : "opacity-0 group-hover:opacity-50")} />
                  </button>
                );
              })}
            </div>

            {/* Content Area */}
            <div className="md:col-span-3 space-y-6">
              {selectedLayoutModule === 'general' ? (
                <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
                  <div className="p-6 border-b bg-muted/30 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Library className="w-5 h-5 text-primary" />
                        Theme Library
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Global branding themes that can be applied to any module.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingTheme(null);
                          setNewTheme({
                            name: '',
                            logoUrl: '',
                            primaryColor: '#000000',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            invoiceFooterText: '',
                            organizationDetails: '',
                            logoOverrideUrl: '',
                            logoSize: 'Medium',
                            logoAlignment: 'Right',
                            infoAlignment: 'Left',
                            showBankDetails: false,
                            bankAccounts: [],
                            paymentInstructions: '',
                            enableWatermark: false,
                            watermarkType: 'Text Only',
                            watermarkOpacity: 8,
                            autoStatusText: true
                          });
                          setIsThemeModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-[#0ea5e9] text-white px-4 py-2 rounded-lg font-bold text-xs hover:opacity-90 transition-opacity"
                      >
                        <Plus className="w-4 h-4" />
                        Create Theme
                      </button>
                      <button
                        onClick={fetchBrandingThemes}
                        className="flex items-center gap-2 border px-4 py-2 rounded-lg font-bold text-xs hover:bg-muted transition-colors"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {brandingThemes.length === 0 ? (
                        <div className="sm:col-span-2 p-12 text-center border-2 border-dashed rounded-xl opacity-50 italic text-sm">
                          No branding themes created yet.
                        </div>
                      ) : (
                        brandingThemes.map((theme) => (
                          <div key={theme.id} className="p-4 border rounded-xl flex items-center justify-between hover:bg-muted/30 transition-colors group">
                            <div className="flex items-center gap-4">
                              {theme.logoUrl ? (
                                <img src={theme.logoUrl} alt={theme.name || ''} className="w-10 h-10 object-contain rounded border bg-white" referrerPolicy="no-referrer" />
                              ) : (
                                <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center text-[8px] font-bold opacity-30">LOGO</div>
                              )}
                              <div>
                                <h4 className="font-bold text-xs">{theme.name}</h4>
                                <div 
                                  className="w-8 h-1 mt-1 rounded-full" 
                                  style={{ backgroundColor: theme.primaryColor }}
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => {
                                  setSelectedPreviewTheme(theme);
                                  setSelectedPreviewModule('Tax Invoice');
                                  setIsPreviewModalOpen(true);
                                }}
                                className="p-1.5 hover:bg-muted text-muted-foreground rounded-lg"
                              >
                                <Monitor className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingTheme(theme);
                                  setNewTheme({
                                    name: theme.name || '',
                                    logoUrl: theme.logoUrl || '',
                                    primaryColor: theme.primaryColor || '#000000',
                                    fontFamily: theme.fontFamily || '',
                                    invoiceFooterText: theme.invoiceFooterText,
                                    organizationDetails: theme.organizationDetails,
                                    logoOverrideUrl: theme.logoOverrideUrl || '',
                                    logoSize: theme.logoSize || 'Medium',
                                    logoAlignment: theme.logoAlignment || 'Right',
                                    infoAlignment: theme.infoAlignment || 'Left',
                                    showBankDetails: theme.showBankDetails || false,
                                    bankAccounts: theme.bankAccounts || [],
                                    paymentInstructions: theme.paymentInstructions || '',
                                    enableWatermark: theme.enableWatermark || false,
                                    watermarkType: theme.watermarkType || 'Text Only',
                                    watermarkOpacity: theme.watermarkOpacity ?? 8,
                                    autoStatusText: theme.autoStatusText ?? true
                                  });
                                  setIsThemeModalOpen(true);
                                }}
                                className="p-1.5 hover:bg-primary/10 text-primary rounded-lg"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteTheme(theme.id)}
                                className="p-1.5 hover:bg-destructive/10 text-destructive rounded-lg"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-card border rounded-xl overflow-hidden shadow-sm animate-in zoom-in-95 duration-200">
                  <div className="p-6 border-b bg-muted/30">
                    <h2 className="text-lg font-semibold flex items-center gap-2 capitalize">
                      {String(selectedLayoutModule || '').replace(/([A-Z])/g, ' $1')} Layout
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Customize how {selectedLayoutModule} print-outs appear to your customers.
                    </p>
                  </div>
                  
                  <div className="p-8 space-y-10">
                    <div className="flex flex-col gap-4">
                      <label className="text-sm font-bold uppercase tracking-wider opacity-60">Assigned Theme</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {brandingThemes.map((theme) => {
                          const themeKey = `${selectedLayoutModule}ThemeId` as keyof OrganizationSettings['layouts'];
                          const isAssigned = settings.layouts?.[themeKey] === theme.id || 
                                           (theme.id === 'Standard' && !settings.layouts?.[themeKey]);
                          
                          return (
                            <button
                              key={theme.id}
                              onClick={() => {
                                const newLayouts = { ...settings.layouts, [themeKey]: theme.id };
                                setSettings({ ...settings, layouts: newLayouts });
                                // Save to Firebase
                                if (orgId) {
                                  updateDoc(doc(db, 'organizations', orgId), {
                                    'settings.layouts': newLayouts
                                  });
                                }
                              }}
                              className={cn(
                                "flex items-center gap-4 p-4 border-2 rounded-2xl transition-all text-left group",
                                isAssigned ? "border-primary bg-primary/5" : "border-muted hover:border-primary/50 hover:bg-muted/30"
                              )}
                            >
                              <div className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                                isAssigned ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                              )}>
                                {isAssigned ? <CheckCircle2 className="w-5 h-5" /> : <Library className="w-5 h-5" />}
                              </div>
                              <div>
                                <p className="font-bold text-sm">{theme.name}</p>
                                <p className="text-[10px] text-muted-foreground uppercase">{(theme.fontFamily || '').split(',')[0]}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Preview specific to this module */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-bold uppercase tracking-wider opacity-60">Live Preview</label>
                        <button
                          onClick={() => {
                            const themeId = settings.layouts?.[`${selectedLayoutModule}ThemeId` as keyof OrganizationSettings['layouts']] || 'Standard';
                            const theme = brandingThemes.find(t => t.id === themeId) || brandingThemes[0];
                            if (theme) {
                              const moduleTitle = selectedLayoutModule === 'quotation' ? 'Quotation' : 
                                               selectedLayoutModule === 'delivery' ? 'Delivery Note' :
                                               selectedLayoutModule === 'purchaseOrder' ? 'Purchase Order' :
                                               selectedLayoutModule === 'bill' ? 'Bill' : 'Tax Invoice';
                              setSelectedPreviewTheme(theme);
                              setSelectedPreviewModule(moduleTitle);
                              setIsPreviewModalOpen(true);
                            }
                          }}
                          className="text-xs font-bold text-primary hover:underline"
                        >
                          View Full Screen Preview
                        </button>
                      </div>
                      
                      <div className="border-2 border-dashed rounded-3xl p-8 bg-muted/10 relative overflow-hidden">
                        {/* Simple Mock Print Out */}
                        <div className="bg-white rounded-xl shadow-2xl p-6 min-h-[300px] flex flex-col gap-6">
                           {/* Layout preview logic based on assigned theme */}
                           {(() => {
                             const themeId = settings.layouts?.[`${selectedLayoutModule}ThemeId` as keyof OrganizationSettings['layouts']] || 'Standard';
                             const theme = brandingThemes.find(t => t.id === themeId) || brandingThemes[0];
                             if (!theme) return null;

                             return (
                               <>
                                 <div className={cn(
                                   "flex flex-col gap-2",
                                   theme.logoAlignment === 'Center' ? "items-center" : 
                                   theme.logoAlignment === 'Right' ? "items-end" : "items-start"
                                 )}>
                                   <div className="w-16 h-8 bg-muted/40 rounded flex items-center justify-center text-[6px] font-black opacity-30">LOGO</div>
                                   <div className={cn(
                                     "text-[8px] space-y-0.5",
                                     theme.infoAlignment === 'Center' ? "text-center" : 
                                     theme.infoAlignment === 'Right' ? "text-right" : "text-left"
                                   )}>
                                     <p className="font-bold" style={{ color: theme.primaryColor }}>{organization?.name}</p>
                                     <p className="opacity-50">Tax ID: 123-456</p>
                                   </div>
                                 </div>
                                 
                                 <div className="flex-1 space-y-3">
                                   <div className="flex justify-between items-end border-b pb-2">
                                     <h3 className="text-lg font-black uppercase tracking-tighter" style={{ color: theme.primaryColor }}>
                                       {selectedLayoutModule === 'quotation' ? 'Quotation' : 
                                        selectedLayoutModule === 'delivery' ? 'Delivery Note' :
                                        selectedLayoutModule === 'purchaseOrder' ? 'Purchase Order' :
                                        selectedLayoutModule === 'bill' ? 'Bill' : 'Tax Invoice'}
                                     </h3>
                                     <div className="text-[8px] text-right">
                                       <p className="font-bold"># {selectedLayoutModule.toUpperCase()}-001</p>
                                       <p className="opacity-50">08 May 2026</p>
                                     </div>
                                   </div>
                                   <div className="space-y-1">
                                     <div className="w-full h-2 bg-muted/20 rounded" />
                                     <div className="w-full h-2 bg-muted/20 rounded" />
                                     <div className="w-3/4 h-2 bg-primary/10 rounded" />
                                   </div>
                                 </div>

                                 <div className="mt-auto pt-4 border-t flex justify-end">
                                   <div className="w-24 h-4 bg-primary rounded shadow-lg shadow-primary/20" />
                                 </div>
                               </>
                             );
                           })()}
                        </div>
                        <p className="text-[10px] text-center mt-6 text-muted-foreground font-bold uppercase tracking-widest">Assign a theme from above to change the layout.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'catalog' ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-card border rounded-2xl p-6 space-y-6">
                <div>
                  <h3 className="font-bold text-lg">Landing Page Style</h3>
                  <p className="text-xs text-muted-foreground">Adjust the visual vibe of your public catalog.</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest opacity-40">Theme Engine</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {['Standard', 'Brutalist', 'Minimal', 'Technical', 'Cherry Blossom'].map((t) => (
                        <button
                          key={t}
                          onClick={() => setCatalogSettings({ ...catalogSettings, theme: t as any })}
                          className={cn(
                            "py-3 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all",
                            catalogSettings.theme === t ? "border-black bg-black text-white" : "border-black/5 hover:border-black/20",
                            t === 'Cherry Blossom' && "border-pink-200 text-pink-700 hover:bg-pink-50"
                          )}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest opacity-40">Hero Title Override</label>
                    <input 
                      type="text"
                      value={catalogSettings.customHeroTitle}
                      onChange={(e) => setCatalogSettings({ ...catalogSettings, customHeroTitle: e.target.value })}
                      placeholder="e.g. THEORY.OFFLINE"
                      className="w-full bg-slate-50 border-2 border-black/5 p-4 rounded-xl outline-none focus:border-black transition-all font-bold text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest opacity-40">Hero Description Override</label>
                    <textarea 
                      value={catalogSettings.customHeroDescription}
                      onChange={(e) => setCatalogSettings({ ...catalogSettings, customHeroDescription: e.target.value })}
                      placeholder="Describe your brand's vision..."
                      rows={4}
                      className="w-full bg-slate-50 border-2 border-black/5 p-4 rounded-xl outline-none focus:border-black transition-all font-bold text-sm resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest opacity-40">WhatsApp Number for Checkout</label>
                    <input 
                      type="text"
                      value={catalogSettings.whatsappNumber}
                      onChange={(e) => setCatalogSettings({ ...catalogSettings, whatsappNumber: e.target.value })}
                      placeholder="e.g. 201234567890"
                      className="w-full bg-slate-50 border-2 border-black/5 p-4 rounded-xl outline-none focus:border-black transition-all font-bold text-sm"
                    />
                    <p className="text-[9px] text-muted-foreground uppercase opacity-60">Include country code without + or spaces (e.g. 2010...)</p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/20 rounded-xl">
                    <div className="space-y-0.5">
                      <p className="text-xs font-bold uppercase">Hide Prices</p>
                      <p className="text-[10px] text-muted-foreground">Show 'Contact for Price' instead of values</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={catalogSettings.hidePrices}
                        onChange={(e) => setCatalogSettings({ ...catalogSettings, hidePrices: e.target.checked })}
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="bg-black text-white p-8 rounded-2xl space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-lg">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                  </div>
                  <h4 className="font-bold">Pro Tip: Brutalism</h4>
                </div>
                <p className="text-xs text-white/60 leading-relaxed uppercase tracking-tight">
                  The Brutalist theme thrives on bold typography and high contrast. Use short, punchy hero titles for the best impact.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white border rounded-2xl overflow-hidden flex flex-col h-full min-h-[600px]">
                <div className="p-4 bg-muted/30 border-b flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Live Preview (Mobile Visualization)</span>
                  <div className="flex items-center gap-4">
                    <Monitor className="w-4 h-4 opacity-20" />
                    <Smartphone className="w-4 h-4" />
                  </div>
                </div>
                
                <div className="flex-1 bg-slate-100 flex items-center justify-center p-8">
                  <div className="w-[320px] h-[550px] bg-white border-8 border-black rounded-[3rem] shadow-2xl overflow-hidden relative scale-90 origin-center">
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-b-2xl z-10" />
                    
                    <div className="h-full overflow-y-auto no-scrollbar pt-12 p-6 flex flex-col">
                      <div className="flex items-center justify-between mb-8">
                        <h4 className="text-sm font-black tracking-tighter uppercase">{organization?.name || 'CATALOG'}.</h4>
                        <ShoppingCart className="w-4 h-4" />
                      </div>
                      
                      <div className="flex-1">
                        <h2 className="text-4xl font-black tracking-tighter uppercase leading-[0.8] mb-4">
                          {catalogSettings.customHeroTitle || 'THEORY.STREET'}
                        </h2>
                        <p className="text-[9px] font-bold opacity-40 uppercase leading-relaxed mb-6">
                          {catalogSettings.customHeroDescription || 'Technical foundations for the urban explorer.'}
                        </p>
                        
                        <div className="bg-black text-white py-3 rounded-full text-[9px] font-black uppercase tracking-widest text-center">
                          Explore
                        </div>

                        <div className="mt-12 space-y-4">
                          <div className="aspect-[4/5] bg-slate-50 border-2 border-black p-4 flex flex-col justify-end">
                            <div className="space-y-1">
                              <p className="text-xs font-black uppercase tracking-tighter">Obsidian Trench</p>
                              <p className="text-[10px] font-bold italic opacity-40">EGP 2,500.00</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 bg-muted/10 border-t flex items-center justify-between">
                  {(() => {
                    const urlWithoutHash = window.location.href.split('#')[0];
                    const cleanBaseUrl = urlWithoutHash.endsWith('/') ? urlWithoutHash.slice(0, -1) : urlWithoutHash;
                    const catalogUrl = `${cleanBaseUrl}/#/catalog/${orgId || localStorage.getItem('selected_org_id') || ''}`;
                    return (
                      <>
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-primary/10 rounded-xl">
                            <Globe className="w-5 h-5 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-black uppercase">Public Link</p>
                            <p className="text-[10px] text-muted-foreground font-mono truncate">{catalogUrl}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => window.open(catalogUrl, '_blank')}
                          className="flex items-center gap-2 bg-white border border-black/10 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:border-black/30 transition-all shadow-sm shrink-0"
                        >
                          Open Catalog <ArrowRight className="w-3 h-3" />
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'access-rights' ? (
        <UserAccessRightsView orgId={orgId} />
      ) : (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="p-6 border-b bg-muted/30">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <UsersIcon className="w-5 h-5 text-primary" />
                Staff Management
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage users who have access to your organization and their permissions.
              </p>
            </div>
            <div className="divide-y">
              {staffList.length === 0 ? (
                <div className="p-12 text-center">
                  <UsersIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                  <p className="text-lg font-medium">No staff members found</p>
                  <p className="text-sm text-muted-foreground">Add your first staff member to get started.</p>
                </div>
              ) : (
                staffList.map((staff) => {
                  const membership = staff.memberships.find(m => m.orgId === orgId);
                  return (
                    <div key={staff.id} className="p-6 flex items-center justify-between hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                          {staff.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-bold">{staff.fullName}</h3>
                          <p className="text-sm text-muted-foreground">{staff.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-right">
                          <span className={cn(
                            "px-2 py-1 rounded text-[10px] font-bold uppercase",
                            membership?.role === 'admin' ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                          )}>
                            {membership?.role}
                          </span>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {membership?.permissions.length} Permissions
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingStaff(staff);
                              setNewStaff({
                                fullName: staff.fullName,
                                email: staff.email,
                                role: membership?.role || 'user',
                                permissions: membership?.permissions || ['dashboard']
                              });
                              setIsStaffModalOpen(true);
                            }}
                            className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteStaff(staff.id)}
                            className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Invoice Preview Modal */}
      {isPreviewModalOpen && selectedPreviewTheme && (
        <div
          className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPreviewModalOpen(false);
          }}
        >
          <div className="bg-[#f8fafc] border rounded-3xl shadow-2xl w-full max-w-4xl max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-300">
            <div className="p-4 border-b bg-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Print Layout Preview</h3>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{selectedPreviewTheme.name}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsPreviewModalOpen(false)} 
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-12">
              <div 
                className="bg-white shadow-xl mx-auto w-full max-w-[800px] min-h-[1000px] p-12 flex flex-col gap-12 relative overflow-hidden"
                style={{ fontFamily: selectedPreviewTheme.fontFamily || 'Helvetica, Arial, sans-serif' }}
              >
                {selectedPreviewTheme.enableWatermark && selectedPreviewTheme.autoStatusText && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] rotate-[-45deg] select-none">
                    <span className="text-[150px] font-black tracking-tighter">PAID</span>
                  </div>
                )}

                <div className={cn(
                  "flex flex-col gap-8",
                  selectedPreviewTheme.logoAlignment === 'Center' ? "items-center" : 
                  selectedPreviewTheme.logoAlignment === 'Right' ? "items-end" : "items-start"
                )}>
                  <div className="flex flex-col gap-2">
                    {selectedPreviewTheme.logoOverrideUrl || selectedPreviewTheme.logoUrl ? (
                      <img 
                        src={selectedPreviewTheme.logoOverrideUrl || selectedPreviewTheme.logoUrl} 
                        alt="Company Logo" 
                        className={cn(
                          "object-contain",
                          selectedPreviewTheme.logoSize === 'Small' ? 'h-12 w-auto' : 
                          selectedPreviewTheme.logoSize === 'Medium' ? 'h-16 w-auto' : 'h-24 w-auto'
                        )}
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-24 h-12 bg-muted rounded flex items-center justify-center text-[10px] font-bold opacity-20">LOGO</div>
                    )}
                  </div>

                  <div className={cn(
                    "text-xs space-y-1",
                    selectedPreviewTheme.infoAlignment === 'Center' ? "text-center" : 
                    selectedPreviewTheme.infoAlignment === 'Right' ? "text-right" : "text-left"
                  )}>
                    <h1 className="text-xl font-bold uppercase tracking-tight" style={{ color: selectedPreviewTheme.primaryColor }}>{organization?.name || 'Your Company Name'}</h1>
                    <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
                      {selectedPreviewTheme.organizationDetails || '123 Business Avenue, Tech Park\nCairo, Egypt\nTax Registration: 123-456-789\ntel: +20 100 000 0000'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 pt-12 border-t">
                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] font-bold uppercase opacity-40">Bill To</label>
                      <p className="font-bold text-sm">Customer Name Ltd.</p>
                      <p className="text-[11px] text-muted-foreground whitespace-pre-line leading-relaxed">456 Customer Road\nLondon, UK</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-right">
                    <h2 className="text-3xl font-black uppercase tracking-tighter" style={{ color: selectedPreviewTheme.primaryColor }}>
                      {selectedPreviewModule || 'Tax Invoice'}
                    </h2>
                    <div className="space-y-1">
                      <div className="flex justify-end gap-4 text-[11px]">
                        <span className="opacity-50">Invoice Number</span>
                        <span className="font-bold">INV-001</span>
                      </div>
                      <div className="flex justify-end gap-4 text-[11px]">
                        <span className="opacity-50">Invoice Date</span>
                        <span className="font-bold">08 May 2026</span>
                      </div>
                      <div className="flex justify-end gap-4 text-[11px]">
                        <span className="opacity-50">Due Date</span>
                        <span className="font-bold">07 June 2026</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 mt-8">
                   <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b-2 border-black/5 pb-2 text-[10px] font-bold uppercase opacity-40">
                        <th className="py-2">Description</th>
                        <th className="py-2 text-right">Qty</th>
                        <th className="py-2 text-right">Unit Price</th>
                        <th className="py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-black/5">
                        <td className="py-4">Professional Consulting Services - Q2 Strategy</td>
                        <td className="py-4 text-right">1.00</td>
                        <td className="py-4 text-right">1,500.00</td>
                        <td className="py-4 text-right font-bold">1,500.00</td>
                      </tr>
                      <tr className="border-b border-black/5">
                        <td className="py-4">Custom Template Development</td>
                        <td className="py-4 text-right">4.00</td>
                        <td className="py-4 text-right">250.00</td>
                        <td className="py-4 text-right font-bold">1,000.00</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end pt-8">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="opacity-50">Subtotal</span>
                      <span className="font-bold">2,500.00</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="opacity-50">Tax (14%)</span>
                      <span className="font-bold">350.00</span>
                    </div>
                    <div className="flex justify-between border-t border-black pt-4">
                      <span className="font-bold uppercase">Total EGP</span>
                      <span className="text-xl font-black" style={{ color: selectedPreviewTheme.primaryColor }}>2,850.00</span>
                    </div>
                  </div>
                </div>

                <div className="mt-auto space-y-8 pt-12 border-t">
                  <div className="grid grid-cols-2 gap-8">
                    {selectedPreviewTheme.showBankDetails && selectedPreviewTheme.bankAccounts && selectedPreviewTheme.bankAccounts.length > 0 && (
                      <div className="space-y-2 p-4 bg-muted/20 rounded-xl">
                        <h4 className="text-[10px] font-bold uppercase opacity-40">Payment Details</h4>
                        {selectedPreviewTheme.bankAccounts.map((acc, idx) => (
                           <div key={idx} className="text-[10px] space-y-0.5">
                            <p className="font-bold">{acc.label || 'Bank Account'}</p>
                            <p>{acc.bankDetails} - {acc.accountName}</p>
                            <p>Acc: {acc.accountNumber}</p>
                            <p>IBAN: {acc.iban}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedPreviewTheme.paymentInstructions && (
                      <div className="space-y-2">
                        <h4 className="text-[10px] font-bold uppercase opacity-40">Notes</h4>
                        <p className="text-[10px] leading-relaxed opacity-60">{selectedPreviewTheme.paymentInstructions}</p>
                      </div>
                    )}
                  </div>

                  <div className="text-center pt-8 border-t border-black/5">
                    <p className="text-[10px] italic opacity-40">{selectedPreviewTheme.invoiceFooterText || 'Thank you for your business!'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-white flex justify-end gap-4">
              <button
                onClick={() => window.print()}
                className="px-6 py-2.5 rounded-xl font-bold uppercase text-xs hover:bg-muted transition-colors flex items-center gap-2"
              >
                Print Preview
              </button>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branding Theme Modal */}
      {isThemeModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsThemeModalOpen(false);
          }}
        >
          <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-2xl max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b flex items-center justify-between bg-muted/30">
              <h3 className="text-xl font-bold">{editingTheme ? 'Edit Branding Theme' : 'Create Branding Theme'}</h3>
              <button onClick={() => setIsThemeModalOpen(false)} className="p-2 hover:bg-muted rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Theme Name</label>
                  <input
                    type="text"
                    value={newTheme.name || ''}
                    onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })}
                    placeholder="Standard"
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Logo URL</label>
                  <input
                    type="text"
                    value={newTheme.logoUrl || ''}
                    onChange={(e) => setNewTheme({ ...newTheme, logoUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>
              </div>

              <div className="p-6 border rounded-2xl bg-muted/10 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-sm">Upload Logo</h4>
                    <p className="text-[10px] text-muted-foreground">Upload an image and we will set the Logo URL automatically</p>
                  </div>
                  <input
                    type="file"
                    ref={logoInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button 
                    onClick={() => logoInputRef.current?.click()}
                    className="flex items-center gap-2 bg-white border px-4 py-2 rounded-xl font-bold text-xs hover:bg-muted transition-colors"
                  >
                    <Upload className="w-3 h-3" />
                    Upload
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Primary Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newTheme.primaryColor || '#000000'}
                      onChange={(e) => setNewTheme({ ...newTheme, primaryColor: e.target.value })}
                      className="w-10 h-10 rounded border bg-background p-1 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={newTheme.primaryColor || '#000000'}
                      onChange={(e) => setNewTheme({ ...newTheme, primaryColor: e.target.value })}
                      className="flex-1 px-4 py-2 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Font Family</label>
                  <input
                    type="text"
                    value={newTheme.fontFamily || ''}
                    onChange={(e) => setNewTheme({ ...newTheme, fontFamily: e.target.value })}
                    placeholder="Helvetica, Arial, sans-serif"
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Invoice Footer Text</label>
                <textarea
                  value={newTheme.invoiceFooterText}
                  onChange={(e) => setNewTheme({ ...newTheme, invoiceFooterText: e.target.value })}
                  placeholder="Thank you for your business..."
                  rows={3}
                  className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Organization Details</label>
                <textarea
                  value={newTheme.organizationDetails}
                  onChange={(e) => setNewTheme({ ...newTheme, organizationDetails: e.target.value })}
                  placeholder="Address, Phone, Website, etc."
                  rows={3}
                  className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                />
              </div>

              <div className="pt-6 border-t space-y-6">
                <div>
                  <h4 className="text-lg font-bold">Invoice Print Settings</h4>
                  <p className="text-xs text-muted-foreground">Customize printed invoice layout per branding theme (logo override and bank details)</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase opacity-50">Logo Override URL</label>
                    <input
                      type="text"
                      value={newTheme.logoOverrideUrl}
                      onChange={(e) => setNewTheme({ ...newTheme, logoOverrideUrl: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                    <p className="text-[10px] text-muted-foreground">Leave empty to use the branding theme logo</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase opacity-50">Logo Size</label>
                    <CustomSelect
                      value={newTheme.logoSize}
                      onChange={(e) => setNewTheme({ ...newTheme, logoSize: e.target.value as any })}
                      className="px-4 py-2.5 border rounded-xl bg-background"
                      containerClassName="p-0 space-y-0"
                    >
                      <option value="Small">Small</option>
                      <option value="Medium">Medium</option>
                      <option value="Large">Large</option>
                    </CustomSelect>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase opacity-50">Logo Alignment</label>
                    <div className="flex gap-1 p-1 bg-muted/30 rounded-xl border">
                      {['Left', 'Center', 'Right'].map((align) => (
                        <button
                          key={align}
                          onClick={() => setNewTheme({ ...newTheme, logoAlignment: align as any })}
                          className={cn(
                            "flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all",
                            newTheme.logoAlignment === align ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:bg-white/50"
                          )}
                        >
                          {align}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase opacity-50">Company Info Alignment</label>
                    <div className="flex gap-1 p-1 bg-muted/30 rounded-xl border">
                      {['Left', 'Center', 'Right'].map((align) => (
                        <button
                          key={align}
                          onClick={() => setNewTheme({ ...newTheme, infoAlignment: align as any })}
                          className={cn(
                            "flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all",
                            newTheme.infoAlignment === align ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:bg-white/50"
                          )}
                        >
                          {align}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-4 border-2 border-dashed rounded-2xl bg-slate-50 space-y-4">
                  <div className="flex items-center justify-between">
                    <h5 className="text-xs font-bold uppercase tracking-wider opacity-50">Layout Preview</h5>
                    <div className="flex gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <div className="w-2 h-2 rounded-full bg-yellow-400" />
                      <div className="w-2 h-2 rounded-full bg-green-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white border rounded-lg p-6 shadow-sm min-h-[200px] flex flex-col gap-6 overflow-hidden">
                    <div className={cn(
                      "flex flex-col gap-4",
                      newTheme.logoAlignment === 'Center' ? "items-center" : 
                      newTheme.logoAlignment === 'Right' ? "items-end" : "items-start"
                    )}>
                      {newTheme.logoUrl || newTheme.logoOverrideUrl ? (
                         <img 
                          src={newTheme.logoOverrideUrl || newTheme.logoUrl} 
                          alt="Logo Preview" 
                          className={cn(
                            "object-contain max-h-12 border rounded p-1",
                            newTheme.logoSize === 'Small' ? 'w-16' : 
                            newTheme.logoSize === 'Medium' ? 'w-24' : 'w-32'
                          )} 
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-24 h-12 bg-muted rounded flex items-center justify-center text-[10px] uppercase font-bold opacity-20">Logo</div>
                      )}
                    </div>

                    <div className={cn(
                      "text-xs space-y-1",
                      newTheme.infoAlignment === 'Center' ? "text-center" : 
                      newTheme.infoAlignment === 'Right' ? "text-right" : "text-left"
                    )}>
                      <p className="font-bold">{organization?.name || 'Company Name'}</p>
                      <div className="text-muted-foreground whitespace-pre-line text-[10px]">
                        {newTheme.organizationDetails || '123 Business Street\nCity, Country\nTax ID: 123-456-789'}
                      </div>
                    </div>

                    <div className="border-t pt-4 grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <div className="w-full h-1 bg-muted rounded" />
                        <div className="w-2/3 h-1 bg-muted rounded" />
                      </div>
                      <div className="space-y-1 flex flex-col items-end">
                        <div className="w-24 h-4 bg-muted rounded" />
                        <div className="w-16 h-3 bg-muted rounded opacity-50" />
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] text-center text-muted-foreground uppercase font-medium">Use the alignment selectors above to "move" elements around.</p>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="showBankDetails"
                    checked={newTheme.showBankDetails}
                    onChange={(e) => setNewTheme({ ...newTheme, showBankDetails: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="showBankDetails" className="flex flex-col">
                    <span className="text-sm font-bold">Show Bank Details</span>
                    <span className="text-[10px] text-muted-foreground">Display bank details block on printed invoice</span>
                  </label>
                </div>

                {newTheme.showBankDetails && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-bold uppercase tracking-wider opacity-50">Bank Details</h5>
                    </div>

                    <div className="space-y-4">
                      {newTheme.bankAccounts.map((account, index) => (
                        <div key={index} className="p-6 border rounded-2xl bg-muted/5 space-y-4 relative">
                          <div className="flex items-center justify-between">
                            <h6 className="font-bold text-sm">Account {index + 1}</h6>
                            <button
                              onClick={() => {
                                const updatedAccounts = [...newTheme.bankAccounts];
                                updatedAccounts.splice(index, 1);
                                setNewTheme({ ...newTheme, bankAccounts: updatedAccounts });
                              }}
                              className="flex items-center gap-2 text-destructive hover:bg-destructive/10 px-3 py-1.5 rounded-lg transition-colors text-xs font-bold"
                            >
                              <Trash2 className="w-3 h-3" />
                              Remove
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Label (optional)</label>
                              <input
                                type="text"
                                value={account.label}
                                onChange={(e) => {
                                  const updatedAccounts = [...newTheme.bankAccounts];
                                  updatedAccounts[index] = { ...updatedAccounts[index], label: e.target.value };
                                  setNewTheme({ ...newTheme, bankAccounts: updatedAccounts });
                                }}
                                placeholder="Main Account"
                                className="w-full px-4 py-2 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Bank Details</label>
                              <input
                                type="text"
                                value={account.bankDetails}
                                onChange={(e) => {
                                  const updatedAccounts = [...newTheme.bankAccounts];
                                  updatedAccounts[index] = { ...updatedAccounts[index], bankDetails: e.target.value };
                                  setNewTheme({ ...newTheme, bankAccounts: updatedAccounts });
                                }}
                                placeholder="QNB"
                                className="w-full px-4 py-2 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Account Name</label>
                              <input
                                type="text"
                                value={account.accountName}
                                onChange={(e) => {
                                  const updatedAccounts = [...newTheme.bankAccounts];
                                  updatedAccounts[index] = { ...updatedAccounts[index], accountName: e.target.value };
                                  setNewTheme({ ...newTheme, bankAccounts: updatedAccounts });
                                }}
                                placeholder="Demo test"
                                className="w-full px-4 py-2 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Bank Account Number</label>
                              <input
                                type="text"
                                value={account.accountNumber}
                                onChange={(e) => {
                                  const updatedAccounts = [...newTheme.bankAccounts];
                                  updatedAccounts[index] = { ...updatedAccounts[index], accountNumber: e.target.value };
                                  setNewTheme({ ...newTheme, bankAccounts: updatedAccounts });
                                }}
                                placeholder="12345678"
                                className="w-full px-4 py-2 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">IBAN</label>
                              <input
                                type="text"
                                value={account.iban}
                                onChange={(e) => {
                                  const updatedAccounts = [...newTheme.bankAccounts];
                                  updatedAccounts[index] = { ...updatedAccounts[index], iban: e.target.value };
                                  setNewTheme({ ...newTheme, bankAccounts: updatedAccounts });
                                }}
                                placeholder="QA00022244090900"
                                className="w-full px-4 py-2 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">SWIFT</label>
                              <input
                                type="text"
                                value={account.swift}
                                onChange={(e) => {
                                  const updatedAccounts = [...newTheme.bankAccounts];
                                  updatedAccounts[index] = { ...updatedAccounts[index], swift: e.target.value };
                                  setNewTheme({ ...newTheme, bankAccounts: updatedAccounts });
                                }}
                                placeholder="ABCQAQA"
                                className="w-full px-4 py-2 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => {
                        setNewTheme({
                          ...newTheme,
                          bankAccounts: [
                            ...newTheme.bankAccounts,
                            { label: '', bankDetails: '', accountName: '', accountNumber: '', iban: '', swift: '' }
                          ]
                        });
                      }}
                      className="flex items-center gap-2 border px-4 py-2 rounded-xl font-bold text-xs hover:bg-muted transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Add Bank Account
                    </button>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Payment Instructions (optional)</label>
                      <textarea
                        value={newTheme.paymentInstructions}
                        onChange={(e) => setNewTheme({ ...newTheme, paymentInstructions: e.target.value })}
                        placeholder="Please reference invoice number on transfer."
                        rows={3}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                      />
                    </div>

                    <div className="pt-6 border-t space-y-4">
                      <h5 className="text-sm font-bold uppercase tracking-wider opacity-50">Watermark</h5>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="enableWatermark"
                          checked={newTheme.enableWatermark}
                          onChange={(e) => setNewTheme({ ...newTheme, enableWatermark: e.target.checked })}
                          className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <label htmlFor="enableWatermark" className="flex flex-col">
                          <span className="text-sm font-bold">Enable Watermark</span>
                          <span className="text-[10px] text-muted-foreground">Show a watermark overlay on printed invoices</span>
                        </label>
                      </div>

                      {newTheme.enableWatermark && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                          <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label className="text-xs font-bold uppercase opacity-50">Watermark Type</label>
                              <CustomSelect
                                value={newTheme.watermarkType}
                                onChange={(e) => setNewTheme({ ...newTheme, watermarkType: e.target.value as any })}
                                className="px-4 py-2.5 border rounded-xl bg-background"
                                containerClassName="p-0 space-y-0"
                              >
                                <option value="Text Only">Text Only</option>
                                <option value="Image">Image</option>
                              </CustomSelect>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <label className="text-xs font-bold uppercase opacity-50">Watermark Opacity</label>
                                <span className="text-xs font-bold">{newTheme.watermarkOpacity}%</span>
                              </div>
                              <input
                                type="range"
                                min="1"
                                max="100"
                                value={newTheme.watermarkOpacity}
                                onChange={(e) => setNewTheme({ ...newTheme, watermarkOpacity: parseInt(e.target.value) })}
                                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                              />
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id="autoStatusText"
                              checked={newTheme.autoStatusText}
                              onChange={(e) => setNewTheme({ ...newTheme, autoStatusText: e.target.checked })}
                              className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <label htmlFor="autoStatusText" className="flex flex-col">
                              <span className="text-sm font-bold">Auto Status Text</span>
                              <span className="text-[10px] text-muted-foreground">Automatically show DRAFT, PAID, or VOID based on invoice status</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t bg-muted/30 flex justify-end gap-4">
              <button
                onClick={() => setIsThemeModalOpen(false)}
                className="px-6 py-2.5 rounded-xl font-bold uppercase text-xs hover:bg-muted transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleSaveTheme}
                disabled={saving}
                className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : editingTheme ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Modal */}
      {isStaffModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsStaffModalOpen(false);
          }}
        >
          <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-2xl max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b flex items-center justify-between bg-muted/30">
              <div>
                <h3 className="text-xl font-bold">{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</h3>
                <p className="text-sm text-muted-foreground">Configure access level and permissions</p>
              </div>
              <button onClick={() => setIsStaffModalOpen(false)} className="p-2 hover:bg-muted rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Full Name</label>
                  <input
                    type="text"
                    value={newStaff.fullName}
                    onChange={(e) => setNewStaff({ ...newStaff, fullName: e.target.value })}
                    placeholder="John Doe"
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Email Address</label>
                  <input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    placeholder="john@example.com"
                    disabled={!!editingStaff}
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all disabled:opacity-50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Access Level</label>
                <CustomSelect
                  value={newStaff.role}
                  onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value as any })}
                  className="px-4 py-2.5 border rounded-xl bg-background"
                  containerClassName="p-0 space-y-0"
                >
                  <option value="user">Standard User</option>
                  <option value="admin">Organization Admin</option>
                </CustomSelect>
                <p className="text-[10px] text-muted-foreground">Admins can manage other staff and organization settings.</p>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <label className="text-xs font-bold uppercase opacity-50">Permissions</label>
                <div className="grid grid-cols-2 gap-4">
                  {MODULES.filter(m => m.id !== 'super_admin').map(module => (
                    <div key={module.id} className="space-y-2 p-4 border rounded-xl bg-muted/20">
                      <label className="flex items-center gap-2 font-bold text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newStaff.permissions.includes(module.id)}
                          onChange={(e) => {
                            const perms = newStaff.permissions;
                            if (e.target.checked) {
                              const subModuleIds = module.subModules.map(sm => sm.id);
                              setNewStaff({ ...newStaff, permissions: Array.from(new Set([...perms, module.id, ...subModuleIds])) });
                            } else {
                              const subModuleIds = module.subModules.map(sm => sm.id);
                              setNewStaff({ ...newStaff, permissions: perms.filter(id => id !== module.id && !subModuleIds.includes(id)) });
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        {module.label}
                      </label>
                      {module.subModules.length > 0 && (
                        <div className="grid grid-cols-1 gap-1 pl-6">
                          {module.subModules.map(subModule => (
                            <label key={subModule.id} className="flex items-center gap-2 text-xs cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                              <input
                                type="checkbox"
                                checked={newStaff.permissions.includes(subModule.id)}
                                onChange={(e) => {
                                  const perms = newStaff.permissions;
                                  if (e.target.checked) {
                                    setNewStaff({ ...newStaff, permissions: [...perms, subModule.id] });
                                  } else {
                                    setNewStaff({ ...newStaff, permissions: perms.filter(id => id !== subModule.id) });
                                  }
                                }}
                                className="w-3 h-3 rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              {subModule.label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-muted/30 flex justify-end gap-4">
              <button
                onClick={() => setIsStaffModalOpen(false)}
                className="px-6 py-2.5 rounded-xl font-bold uppercase text-xs hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStaff}
                disabled={saving}
                className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingStaff ? 'Update Staff' : 'Add Staff'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsView;
